import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Plus, Building2, ExternalLink, Mail, Send } from 'lucide-react';
import Cookies from 'js-cookie';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminStartups() {
  const navigate = useNavigate();
  const [startups, setStartups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStartups();
  }, []);

  const fetchStartups = async () => {
    try {
      const token = Cookies.get('admin_token');
      const response = await axios.get(`${API}/startups/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Filter out listing requests - they appear in separate page
      const activeStartups = (response.data.data || []).filter(
        startup => startup.status !== 'Listing Request'
      );
      setStartups(activeStartups);
    } catch (err) {
      setError('Failed to load startups');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20',
      'Link Sent': 'bg-blue-500/10 text-blue-700 border-blue-500/20',
      'Submitted': 'bg-purple-500/10 text-purple-700 border-purple-500/20',
      'Published': 'bg-green-500/10 text-green-700 border-green-500/20'
    };
    return colors[status] || 'bg-gray-500/10 text-gray-700 border-gray-500/20';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading mb-2">Startups</h1>
          <p className="text-muted-foreground">Manage startup profiles and information</p>
        </div>
        <Button variant="editorial" onClick={() => navigate('/admin/startups/add')}>
          <Plus className="h-4 w-4 mr-2" />
          Add Startup
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
          {error}
        </div>
      )}

      {startups.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>All Startups</CardTitle>
            <CardDescription>View and manage all registered startups</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-12 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="mb-2">No startups added yet</p>
              <p className="text-sm">Click "Add Startup" to create your first entry</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {startups.map((startup) => (
            <Card key={startup.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex gap-4 flex-1">
                    {startup.logo && (
                      <img
                        src={startup.logo}
                        alt={startup.startup_name}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-xl font-bold font-heading mb-1">
                            {startup.startup_name}
                          </h3>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>{startup.industry}</span>
                            <span>•</span>
                            <span>{startup.city}</span>
                            <span>•</span>
                            <span>Founded {startup.founding_year}</span>
                          </div>
                        </div>
                        <Badge className={getStatusColor(startup.status)}>
                          {startup.status}
                        </Badge>
                      </div>

                      <div className="mt-4 space-y-2">
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-muted-foreground">Employees:</span>
                          <span className="font-medium">{startup.employee_count}</span>
                        </div>
                        {startup.website_url && (
                          <div className="flex items-center gap-2 text-sm">
                            <ExternalLink className="h-4 w-4 text-muted-foreground" />
                            <a
                              href={startup.website_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                            >
                              {startup.website_url}
                            </a>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span>Founders: {startup.founders.map(f => f.name).join(', ')}</span>
                        </div>
                      </div>

                      <div className="mt-4 flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/admin/startups/${startup.id}`)}
                        >
                          View Details
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`/submit-story/${startup.story_token}`, '_blank')}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Open Story Form
                        </Button>
                        {startup.status === 'Pending' && (
                          <Button
                            variant="secondary"
                            size="sm"
                          >
                            <Send className="h-4 w-4 mr-2" />
                            Send Story Link
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
